import { FC, ReactNode, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { TbArchive, TbLock } from 'react-icons/tb';

import {
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    WorkspaceResourceGlyph,
} from '@/shared';
import { useFolderStore } from '@/stores';
import { Folder, SetStateMethod } from '@/types';
import { formatDate } from '@/utilities';

interface FolderDestinationDropdownProps {
    children: ReactNode;
    destinationFolder: Folder;
    setDestinationFolder: SetStateMethod<Folder>;
}

const FolderDestinationDropdown: FC<FolderDestinationDropdownProps> = ({
    children,
    destinationFolder,
    setDestinationFolder,
}): JSX.Element => {
    const [input, setInput] = useState('');

    const { folders } = useFolderStore();

    let searchedFolders: Folder[];

    if (input !== '') {
        searchedFolders = folders.filter((folder: Folder) => {
            return (
                // prettier-ignore
                String(folder?.name)?.toLowerCase().includes(String(input)?.toLowerCase())
            );
        });
    } else {
        searchedFolders = folders;
    }

    return (
        <Dropdown
            trigger={['click']}
            popupRender={() => (
                <DropdownPanel
                    maxHeight={16 * 20}
                    minWidth={16 * 20}
                    search={{
                        placeholder: 'Search Folders...',
                        setValue: setInput,
                        value: input,
                    }}
                >
                    <DropdownPanelMenu
                        selectable={false}
                        selectedKeys={destinationFolder?.id ? [destinationFolder.id] : []}
                        onClick={({ key }) => {
                            const nextFolder = folders.find((folder) => folder.id === key);

                            if (nextFolder) {
                                setDestinationFolder(nextFolder);
                            }

                            setInput('');
                        }}
                    >
                        {searchedFolders?.length !== 0 ? (
                            searchedFolders?.map((f: Folder) => {
                                return (
                                    <Menu.Item key={f?.id}>
                                        <DropdownOptionContent
                                            avatar={{
                                                active: destinationFolder?.id === f.id,
                                                resource: 'folder',
                                            }}
                                            metadata={[
                                                `Last updated ${f.updatedAt ? formatDate(f.updatedAt) : 'recently'}`,
                                                f.locked ? (
                                                    <span key='locked'>
                                                        <TbLock style={{ marginRight: '0.25rem' }} />
                                                        Locked
                                                    </span>
                                                ) : null,
                                                f.archived ? (
                                                    <span key='archived'>
                                                        <TbArchive style={{ marginRight: '0.25rem' }} />
                                                        Archived
                                                    </span>
                                                ) : null,
                                            ]}
                                            selected={destinationFolder?.id === f.id}
                                            title={f.name}
                                        />
                                    </Menu.Item>
                                );
                            })
                        ) : (
                            <EmptySection
                                height={16 * 16}
                                icon={<WorkspaceResourceGlyph resource='folders' />}
                                label='No Folders'
                                description={
                                    input
                                        ? 'No folders match your search.'
                                        : 'Create a folder before choosing a destination.'
                                }
                            />
                        )}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            {children}
        </Dropdown>
    );
};

export default FolderDestinationDropdown;
